import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

export default function Projects() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then(r => r.data) });
  const projects = data?.projects || []; const statuses = data?.statuses || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["projects"] });
  const setStatus = async (id, status) => { try { await api.put(`/projects/${id}`, { status }); refresh(); toast.success("Updated"); } catch (e) { toast.error("Failed"); } };
  const remove = async (p) => { await api.delete(`/projects/${p.id}`); refresh(); toast.success("Project deleted"); };

  return (
    <div>
      <PageTitle title="Projects" subtitle="Website & automation project tracking" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setCreating(true)} data-testid="new-project-btn"><Plus className="h-4 w-4" /> New Project</Button>
      } />
      <div className="grid md:grid-cols-2 gap-4">
        {projects.length === 0 ? <p className="text-slate-400 col-span-2 text-center py-8">No projects yet.</p> :
          projects.map((p) => {
            const done = (p.milestones || []).filter(m => m.done).length;
            return (
              <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`project-row-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div><h3 className="font-heading font-semibold text-navy">{p.customerName}</h3><p className="text-xs text-slate-500 font-mono">{p.projectNumber} · {p.projectType}</p></div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-project-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    {canDelete && <ConfirmDelete onConfirm={() => remove(p)} title="Delete this project?" description={`${p.projectNumber} for ${p.customerName} will be removed.`} testid={`project-delete-${p.id}`} />}
                  </div>
                </div>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.scope}</p>
                <p className="mt-2 text-xs text-slate-500">Milestones: {done}/{(p.milestones || []).length}</p>
                <select value={p.status} onChange={e => setStatus(p.id, e.target.value)} className="mt-3 w-full text-xs rounded-md border border-slate-200 px-2 py-1.5" data-testid={`project-status-${p.id}`}>{statuses.map(s => <option key={s}>{s}</option>)}</select>
              </div>
            );
          })}
      </div>
      {creating && <ProjectCreate onClose={() => setCreating(false)} onSaved={() => { refresh(); setCreating(false); }} />}
      {editing && <ProjectEdit project={editing} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </div>
  );
}

function ProjectCreate({ onClose, onSaved }) {
  const [form, setForm] = useState({ customerName: "", customerEmail: "", projectType: "Website", scope: "", targetDate: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => { e.preventDefault(); try { await api.post("/projects", form); toast.success("Project created"); onSaved(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="project-form">
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Customer name" required value={form.customerName} onChange={e => set("customerName", e.target.value)} data-testid="proj-cust-name" />
          <input className={input} type="email" placeholder="Customer email" required value={form.customerEmail} onChange={e => set("customerEmail", e.target.value)} data-testid="proj-cust-email" />
        </div>
        <select className={input} value={form.projectType} onChange={e => set("projectType", e.target.value)} data-testid="proj-type">{["Website", "Automation"].map(t => <option key={t}>{t}</option>)}</select>
        <textarea className={`${input} h-auto py-2`} rows={3} placeholder="Scope" value={form.scope} onChange={e => set("scope", e.target.value)} data-testid="proj-scope" />
        <label className="text-sm text-slate-600 block">Target date<input className={input} type="date" value={form.targetDate} onChange={e => set("targetDate", e.target.value)} /></label>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="proj-save">Create Project</Button>
      </form>
    </DialogContent></Dialog>
  );
}

function ProjectEdit({ project, onClose, onSaved }) {
  const [form, setForm] = useState({ scope: project.scope || "", requirements: project.requirements || "", customerVisibleProgress: project.customerVisibleProgress || "", assignedStaff: project.assignedStaff || "" });
  const [milestones, setMilestones] = useState(project.milestones || []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addM = () => setMilestones([...milestones, { title: "", done: false }]);
  const updM = (i, k, v) => setMilestones(milestones.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const submit = async (e) => { e.preventDefault(); try { await api.put(`/projects/${project.id}`, { ...form, milestones: milestones.filter(m => m.title) }); toast.success("Saved"); onSaved(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{project.projectNumber}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="project-edit-form">
        <label className="text-sm text-slate-600 block">Scope<textarea className={`${input} h-auto py-2`} rows={2} value={form.scope} onChange={e => set("scope", e.target.value)} /></label>
        <label className="text-sm text-slate-600 block">Customer-visible progress<textarea className={`${input} h-auto py-2`} rows={2} value={form.customerVisibleProgress} onChange={e => set("customerVisibleProgress", e.target.value)} data-testid="proj-progress" /></label>
        <div>
          <p className="text-sm text-slate-600 mb-2">Milestones</p>
          {milestones.map((m, i) => (
            <div key={i} className="flex gap-2 items-center mb-2">
              <input type="checkbox" checked={m.done} onChange={e => updM(i, "done", e.target.checked)} />
              <input className={`${input} flex-1`} placeholder="Milestone" value={m.title} onChange={e => updM(i, "title", e.target.value)} />
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addM} className="gap-1.5"><Plus className="h-4 w-4" /> Add milestone</Button>
        </div>
        <input className={input} placeholder="Assigned staff" value={form.assignedStaff} onChange={e => set("assignedStaff", e.target.value)} />
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="proj-edit-save">Save</Button>
      </form>
    </DialogContent></Dialog>
  );
}
